import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Alert } from 'components/ui/alert'
import { UserPlus, Loader2, X, Plus } from 'lucide-react'
import { inviteNewUser } from 'slices/invitedUserSlice'
import { advanceOnboardingStep, ONBOARDING_STEPS } from 'slices/onboardingSlice'

interface InviteStepProps {
	user: any
	onSkip: () => void
}

interface UserInvite {
	name: string
	email: string
	firstName?: string
}

const SERVER_BASE_URL =
	process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : 'https://app.mydealteams.com'

const InviteStep: React.FC<InviteStepProps> = ({ user, onSkip }) => {
	const dispatch = useDispatch()
	const { saving } = useSelector((state: any) => state.onboarding)

	const [invitedUsers, setInvitedUsers] = useState<UserInvite[]>([{ name: '', email: '' }])
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [error, setError] = useState('')
	const [successMessage, setSuccessMessage] = useState('')

	const validateEmail = (email: string): boolean => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
	}

	const handleChange = (index: number, field: 'name' | 'email', value: string) => {
		const updated = [...invitedUsers]
		updated[index] = { ...updated[index], [field]: value }
		if (field === 'name' && value) {
			updated[index].firstName = value.split(' ')[0] || value
		}
		setInvitedUsers(updated)
		setError('')
	}

	const addRow = () => {
		if (invitedUsers.length < 5) {
			setInvitedUsers([...invitedUsers, { name: '', email: '' }])
		}
	}

	const removeRow = (index: number) => {
		if (invitedUsers.length > 1) {
			setInvitedUsers(invitedUsers.filter((_, i) => i !== index))
		}
	}

	const getValidUsers = () =>
		invitedUsers.filter(u => u.name.trim() && validateEmail(u.email.trim()))

	const hasAnyInput = invitedUsers.some(u => u.name.trim() || u.email.trim())
	const allRowsValid = invitedUsers.every(u => {
		const hasName = u.name.trim() !== ''
		const hasEmail = u.email.trim() !== ''
		return (!hasName && !hasEmail) || (hasName && hasEmail && validateEmail(u.email.trim()))
	})

	const handleSubmit = async () => {
		const validUsers = getValidUsers()
		if (validUsers.length === 0) {
			setError('Please add at least one user with a valid name and email.')
			return
		}

		setError('')
		setIsSubmitting(true)

		const pushLink = `${SERVER_BASE_URL}/partners`
		let successCount = 0
		let failCount = 0

		for (const newUser of validUsers) {
			try {
				const firstName = newUser.firstName || newUser.name.split(' ')[0]
				const userToInvite = {
					name: newUser.name.trim(),
					firstName,
					email: newUser.email.trim().toLowerCase(),
				}

				const result: any = await dispatch(
					inviteNewUser({
						newUser: userToInvite,
						inviterId: user._id,
						assetType: '',
						assetId: '',
						pushLink,
					}),
				)

				// The server emails the invitation when it creates it (MYD-547).
				if (result?.payload?._id) {
					successCount++
				} else {
					failCount++
				}
			} catch (err) {
				failCount++
			}
		}

		if (successCount > 0) {
			setSuccessMessage(
				`Successfully invited ${successCount} user${successCount > 1 ? 's' : ''}.${
					failCount > 0 ? ` ${failCount} failed.` : ''
				}`,
			)
		}

		// Short delay to show success message before completing
		setTimeout(() => {
			handleComplete()
		}, 1500)

		setIsSubmitting(false)
	}

	const handleComplete = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.COMPLETE,
				userId: user._id,
			}),
		)
	}

	const handleBack = () => {
		dispatch(
			advanceOnboardingStep({
				nextStep: ONBOARDING_STEPS.TASK_TOUR,
				userId: user._id,
			}),
		)
	}

	return (
		<div className="p-6">
			<div className="flex items-center gap-2 mb-1">
				<UserPlus className="w-5 h-5 text-[#0080fe]" />
				<h2 className="text-lg font-semibold text-[#074f9b]">Invite Your Team</h2>
			</div>
			<p className="text-sm text-gray-500 mb-4">
				Invite up to 5 team members to collaborate on MYDealTeams.
			</p>

			{error && <Alert variant="danger" className="mb-3">{error}</Alert>}
			{successMessage && <Alert variant="success" className="mb-3">{successMessage}</Alert>}

			<div className="space-y-3 mb-4">
				{invitedUsers.map((inviteUser, index) => (
					<div key={index} className="flex items-end gap-2">
						<div className="flex-1 space-y-1">
							<Label className="text-xs">Full Name</Label>
							<Input
								value={inviteUser.name}
								onChange={e => handleChange(index, 'name', e.target.value)}
								placeholder="Full Name"
								className="h-8 text-sm"
							/>
						</div>
						<div className="flex-1 space-y-1">
							<Label className="text-xs">Email</Label>
							<Input
								type="email"
								value={inviteUser.email}
								onChange={e => handleChange(index, 'email', e.target.value)}
								placeholder="email@example.com"
								className="h-8 text-sm"
							/>
						</div>
						{invitedUsers.length > 1 && (
							<Button
								type="button"
								variant="ghost"
								onClick={() => removeRow(index)}
								className="h-8 w-8 p-0 flex-shrink-0"
							>
								<X className="w-3.5 h-3.5" />
							</Button>
						)}
					</div>
				))}
			</div>

			{invitedUsers.length < 5 && (
				<Button
					type="button"
					variant="ghost"
					onClick={addRow}
					className="w-full mb-4 text-sm"
				>
					<Plus className="w-3.5 h-3.5" />
					Add Another
				</Button>
			)}

			<div className="flex gap-2">
				<Button variant="ghost" onClick={handleBack} disabled={saving || isSubmitting} className="flex-1">
					Back
				</Button>
				{hasAnyInput ? (
					<Button
						onClick={handleSubmit}
						disabled={!allRowsValid || isSubmitting || saving}
						className="flex-1"
					>
						{isSubmitting ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Sending...
							</>
						) : (
							'Send Invitations'
						)}
					</Button>
				) : (
					<Button onClick={handleComplete} disabled={saving} className="flex-1">
						Finish Setup
					</Button>
				)}
			</div>
			<div className="flex justify-center mt-2">
				<Button variant="ghost" onClick={handleComplete} disabled={saving} className="text-xs text-gray-400">
					Skip
				</Button>
			</div>
		</div>
	)
}

export default InviteStep
